import { useCallback, useEffect, useState } from "react";
import { IconFace, IconFingerprint, IconLock } from "./icons";
import { registerBiometricCredential, revokeBiometricCredential } from "../lib/biometric-session";
import { isNativeApp } from "../lib/mobile-ui";
import {
  biometricUserMessage,
  getBiometricStatus,
  setBiometricMethod,
  waitForNativeBridge,
  type BiometricMethod,
  type BiometricStatus,
} from "../lib/native-bridge";
import { usePageRefresh } from "../lib/page-lifecycle";

function Toggle({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      className="settings-switch"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
    />
  );
}

export function BiometricSecuritySection({ email }: { email: string }) {
  const [status, setStatus] = useState<BiometricStatus | null>(null);
  const [busy, setBusy] = useState<BiometricMethod | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    const next = await getBiometricStatus();
    setStatus(next);
  }, []);

  usePageRefresh(load);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;
    void (async () => {
      const bridged = await waitForNativeBridge();
      if (cancelled) return;
      setReady(bridged);
      if (bridged) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!isNativeApp() || !ready || !status) return null;

  const rows: Array<{
    method: BiometricMethod;
    available: boolean;
    enabled: boolean;
    title: string;
    hint: string;
    icon: "fingerprint" | "face" | "lock";
  }> = [
    {
      method: "fingerprint",
      available: status.fingerprintAvailable,
      enabled: status.fingerprintEnabled,
      title: "Fingerprint Login",
      hint: "Show a fingerprint button on Sign in and unlock with the fingerprint enrolled on this phone",
      icon: "fingerprint",
    },
    {
      method: "face",
      available: status.faceAvailable,
      enabled: status.faceEnabled,
                  title: `${status.faceLabel} Login`,
      hint: `Use ${status.faceLabel} on this phone. Password stays available as a fallback`,
      icon: "face",
    },
    {
      method: "generic",
      available: status.genericAvailable,
      enabled: status.genericEnabled,
      title: "Biometric Login",
      hint: "Use the fingerprint or face enrolled on this phone. Password stays available as a fallback",
      icon: "lock",
    },
  ];

  const visible = rows.filter((row) => row.available);
  const anyOn = rows.some((row) => row.enabled);

  const apply = async (method: BiometricMethod, enabled: boolean) => {
    setBusy(method);
    setError(null);
    try {
      let token: string | undefined;
      if (enabled && !status.hasCredentials) {
        const issued = await registerBiometricCredential();
        if (!issued) {
          setError("Could not enable biometric login.");
          return;
        }
        token = issued;
      }
      const result = await setBiometricMethod({
        method,
        enabled,
        email,
        token,
      });
      if (result.needsToken && enabled) {
        const issued = await registerBiometricCredential();
        if (!issued) {
          setError("Could not enable biometric login.");
          await load();
          return;
        }
        const retry = await setBiometricMethod({ method, enabled: true, email, token: issued });
        if (!retry.ok) {
          setError(retry.canceled ? null : biometricUserMessage(retry));
          await load();
          return;
        }
        setStatus(retry);
        return;
      }
      if (!result.ok) {
        setError(result.canceled ? null : biometricUserMessage(result));
        await load();
        return;
      }
      setStatus(result);
      if (!result.fingerprintEnabled && !result.faceEnabled && !result.genericEnabled) {
        void revokeBiometricCredential();
      }
    } catch {
      setError("Could not update biometric login.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="settings-group biometric-security">
      <p className="settings-group-label">Biometric Security</p>
      {visible.length === 0 ? (
        <p className="settings-group-note">
          {status.error || "This phone does not have a supported fingerprint or face sensor."}
        </p>
      ) : (
        <>
          <p className="settings-group-note">
            {anyOn
              ? "Fingerprint or Face ID Login adds a button under Sign in. Your phone verifies your biometric, then Dashcam signs in with a device credential. Password stays available as a fallback."
              : "Turn Fingerprint or Face ID Login on after you sign in with email and password. A matching button will appear under Sign in. Password stays available as a fallback."}
          </p>
          {visible.map((row) => (
            <div key={row.method} className="settings-row settings-toggle-row">
              <span className="settings-row-icon">
                {row.icon === "fingerprint" ? <IconFingerprint /> : row.icon === "face" ? <IconFace /> : <IconLock />}
              </span>
              <span className="settings-row-copy">
                <strong>{row.title}</strong>
                <em>{row.hint}</em>
              </span>
              <Toggle
                checked={row.enabled}
                disabled={busy != null}
                label={`${row.enabled ? "Disable" : "Enable"} ${row.title}`}
                onChange={() => void apply(row.method, !row.enabled)}
              />
            </div>
          ))}
        </>
      )}
      {error && <p className="form-error biometric-security-error">{error}</p>}
    </div>
  );
}
